import React, { useMemo } from "react"

import type {
    INetworkStatusRule,
    IPayer,
} from "@common-shared/types/responseData/commonResponseData.types"

import { PopupWrapperElement } from "@front-shared/elements/popupWrapper.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { TooltipElement } from "@front-shared/elements/tooltip.element"
import type { IPopupProps } from "@front-shared/types/props.types"

import { NETWORK_STATUS_CONFIG } from "../../config/networkStatus.config"
import { NetworkStatusHelper } from "../../helpers/networkStatus.helper"

export const NetworkStatusRuleDetailPopupComponent: React.FunctionComponent<
    INetworkStatusRuleDetailPopupProps
> = props => {
    const ruleDisplayData = useMemo(() => {
        return NetworkStatusHelper.getRuleDisplayData(props.rule)
    }, [props.rule])

    const isBluecardMatched = useMemo(() => {
        return Boolean(
            props.rule &&
                "payer" in props.rule &&
                props.bluecardEligible &&
                props.rule.payer.payerId !== props.requestPayer?.payerId &&
                props.rule.payer.payerId !== props.responsePayer?.payerId,
        )
    }, [
        props.rule,
        props.bluecardEligible,
        props.requestPayer,
        props.responsePayer,
    ])

    const renderDetailItem = (
        key: number | string,
        label: string,
        value?: string | null,
        badgeLike?: boolean,
        tooltip?: string,
    ) => {
        const displayValue = value || "-"

        return (
            <div className="flex flex-col gap-[4px]" key={key}>
                <PrimaryText typography="caption">
                    {label}

                    {tooltip && (
                        <TooltipElement
                            className="ml-[4px] align-top"
                            isInlined
                            text={tooltip}
                        />
                    )}
                </PrimaryText>

                {badgeLike ? (
                    <div className="flex">
                        <PrimaryText
                            className="rounded-[4px] px-[8px] py-[4px] bg-brand-offWhite"
                            typography="caption"
                        >
                            {displayValue}
                        </PrimaryText>
                    </div>
                ) : (
                    <PrimaryText weight="medium">{displayValue}</PrimaryText>
                )}
            </div>
        )
    }

    return (
        <PopupWrapperElement
            isActive={props.isActive}
            onDone={props.onDone}
            closeOnOutsideClick
            withCloseIcon
            popupWidthClassName="w-[600px]! md:w-[95vw]! px-[32px]! py-[24px]! bg-brand-white!"
        >
            <div className="flex flex-col gap-[24px]">
                {props.rule ? (
                    <>
                        <PrimaryText typography="h3">
                            {props.rule.name}
                        </PrimaryText>

                        <div className="grid grid-cols-3 gap-y-[20px] gap-x-[16px]">
                            {NETWORK_STATUS_CONFIG.ruleDisplayAlertPreviewKeys.map(
                                (dataKey, index) => {
                                    const config = ruleDisplayData[dataKey]

                                    const tooltip =
                                        isBluecardMatched &&
                                        dataKey === "payerId"
                                            ? "Plan is Bluecard Eligible"
                                            : undefined

                                    return renderDetailItem(
                                        index,
                                        config.label,
                                        config.value,
                                        config.badgeLike,
                                        tooltip,
                                    )
                                },
                            )}
                        </div>
                    </>
                ) : (
                    <PrimaryText typography="h6">
                        Rule details not found
                    </PrimaryText>
                )}
            </div>
        </PopupWrapperElement>
    )
}

interface INetworkStatusRuleDetailPopupProps extends IPopupProps {
    rule: INetworkStatusRule | null
    bluecardEligible?: boolean | null
    requestPayer?: IPayer | null
    responsePayer?: IPayer | null
}
